import React, { useEffect, useState } from "react";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { Link, useNavigate, useParams } from "react-router-dom";
import Config from "../config";
import sample from "../profileImage/sample.png";
import Sidebar from "../components/Sidebar";
import { useMe } from "../hooks/useMe";
import "../styles/profile.scss";
import { UploadFile } from "../components/UploadFile";
import { useMutation, useQuery } from "react-query";
import { Stack } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useDeleteEmployee } from "../hooks/useDeleteEmployee";
import { useArchiveEmployee } from "../hooks/useArchiveEmployee";
import API from "../APIs/API";
import EmployeeTransfer from "./EmployeeTransferModal";

// ─── Password Reset Modal ────────────────────────────────────────────────────
const PasswordResetModal = ({ employee_no, onClose }) => {
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const passwordsMatch = newPwd.length >= 6 && newPwd === confirmPwd;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passwordsMatch) return;
    setLoading(true);
    try {
      await new API().setEmployeePassword(employee_no, newPwd);
      toast.success("Password set successfully!");
      onClose();
    } catch (err) {
      toast.error(err?.message || "Failed to set password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 1300,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 8,
          padding: "32px 36px",
          minWidth: 380,
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ marginBottom: 6 }}>Password Reset</h3>
        <p style={{ color: "#555", fontSize: 13, marginBottom: 20 }}>
          Set a new login password for employee <b>{employee_no}</b>.<br />
          The employee will use their <b>Email ID</b> as username to log in.
        </p>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
              New Password
            </label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                className="form-control"
                type={showNew ? "text" : "password"}
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="Min 6 characters"
                required
                minLength={6}
                style={{ flex: 1 }}
              />
              <span
                style={{ cursor: "pointer", fontSize: 13, color: "#555", whiteSpace: "nowrap" }}
                onClick={() => setShowNew((v) => !v)}
              >
                {showNew ? "Hide" : "Show"}
              </span>
            </div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
              Confirm New Password
            </label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                className="form-control"
                type={showConfirm ? "text" : "password"}
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                placeholder="Re-enter password"
                required
                style={{ flex: 1 }}
              />
              <span
                style={{ cursor: "pointer", fontSize: 13, color: "#555", whiteSpace: "nowrap" }}
                onClick={() => setShowConfirm((v) => !v)}
              >
                {showConfirm ? "Hide" : "Show"}
              </span>
            </div>
            {confirmPwd.length > 0 && !passwordsMatch && (
              <p style={{ color: "#c0392b", fontSize: 12, marginTop: 4 }}>
                {newPwd.length < 6 ? "Password must be at least 6 characters." : "Passwords do not match."}
              </p>
            )}
            {passwordsMatch && (
              <p style={{ color: "#27ae60", fontSize: 12, marginTop: 4 }}>✓ Passwords match</p>
            )}
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button
              type="button"
              className="app-button app-button-gray"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="app-button app-button-primary"
              disabled={!passwordsMatch || loading}
            >
              {loading ? "Setting…" : "Set Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const limitedFilter = createFilterOptions({ limit: 50 });

const initialEmployee = {
  id: 0,
  avatar_src: "",
  user_type: "",
  employee_no: "",
  name_report: "",
  admin_location: "",
  admin_desc: "",
  position_no: "",
  occup_pos_title: "",
  award: "",
  award_desc: "",
  classification: "",
  class_desc: "",
  step_no: "",
  occup_type: "",
  gender: "",
  first_commence: "",
  account_no: "",
  account_no_desc: "",
  emp_status: "",
  paypoint: "",
  paypoint_desc: "",
  date_of_birth: "",
  age: 0,
  occup_pos_cat: "",
  email: "",
  phone_number: "",
  mobile_number: "",
  position_attached: true,
  hda_active: false,
  hda_position_no: "",
  org_executive: "",
  org_wing: "",
  org_division: "",
  org_branch: "",
  org_unit: "",
  creator: "",
  status: true,
  created_at: "",
  updated_at: "",
};

// Plain disabled input — used for always-read-only fields or view mode
const PlainField = ({ label, name, value, disabled = false, editable = false, onChange }) => (
  <div className="form-outline">
    <label className="form-label" htmlFor={name}>
      <b>{label}</b>
    </label>
    <br />
    <input
      className="form-control"
      type="text"
      id={name}
      name={name}
      value={value ?? ""}
      disabled={disabled || !editable}
      onChange={onChange}
    />
  </div>
);

// Autocomplete freeSolo: disabled input in view mode, dropdown+free-text in edit mode
const DropdownField = ({ label, name, value, editable, onChange, options = [] }) => {
  if (!editable) {
    return (
      <div className="form-outline">
        <label className="form-label" htmlFor={name}>
          <b>{label}</b>
        </label>
        <br />
        <input
          className="form-control"
          type="text"
          id={name}
          name={name}
          value={value ?? ""}
          disabled
        />
      </div>
    );
  }
  return (
    <div className="form-outline">
      <label className="form-label" htmlFor={name}>
        <b>{label}</b>
      </label>
      <Autocomplete
        freeSolo
        filterOptions={limitedFilter}
        options={options}
        value={value ?? ""}
        onChange={(_, newValue) =>
          onChange({ target: { name, value: newValue ?? "" } })
        }
        onInputChange={(_, newValue, reason) => {
          if (reason === "input")
            onChange({ target: { name, value: newValue ?? "" } });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            variant="outlined"
            name={name}
            id={name}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
                fontSize: 14,
              },
            }}
          />
        )}
        sx={{ mt: 0.5 }}
      />
    </div>
  );
};

// Position lookup autocomplete: on select fills all 12 position fields from the Staff list.
// Both Position No and Occup Pos Title dropdowns show the full "Position No — Occup Pos Title"
// label for every entry in the Staff list, deduplicated by that combined key.
const PositionLookupField = ({ label, name, value, editable, onPositionSelect, positions, matchKey }) => {
  // Deduplicate by unique position_no + occup_pos_title combination
  const seen = new Set();
  const uniquePositions = positions.filter((p) => {
    const key = `${p.position_no}||${p.occup_pos_title}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const getLabel = (p) =>
    p.position_no && p.occup_pos_title
      ? `${p.position_no} - ${p.occup_pos_title}`
      : p.position_no || p.occup_pos_title || "";

  // Find the currently-selected position object so the dropdown shows it as selected
  const currentPos = uniquePositions.find((p) => p[matchKey] === value) ?? null;

  if (!editable) {
    return (
      <div className="form-outline">
        <label className="form-label" htmlFor={name}>
          <b>{label}</b>
        </label>
        <br />
        <input
          className="form-control"
          type="text"
          id={name}
          name={name}
          value={value ?? ""}
          disabled
        />
      </div>
    );
  }

  return (
    <div className="form-outline">
      <label className="form-label" htmlFor={name}>
        <b>{label}</b>
      </label>
      <Autocomplete
        options={uniquePositions}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : getLabel(option)
        }
        value={currentPos}
        isOptionEqualToValue={(option, val) =>
          option.position_no === val.position_no &&
          option.occup_pos_title === val.occup_pos_title
        }
        onChange={(_, newValue) => {
          if (newValue) onPositionSelect(newValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            variant="outlined"
            name={name}
            id={name}
            placeholder="Select from Staff list..."
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff", fontSize: 14 } }}
          />
        )}
        sx={{ mt: 0.5 }}
      />
    </div>
  );
};

// Date field: formatted plain input in view mode, DatePicker calendar in edit mode
const DateField = ({ label, name, value, editable, onChange }) => {
  const safeDisplay = () => {
    try {
      const d = new Date(value);
      return isNaN(d.getTime()) ? "" : d.toDateString();
    } catch {
      return "";
    }
  };

  if (!editable) {
    return (
      <div className="form-outline">
        <label className="form-label" htmlFor={name}>
          <b>{label}</b>
        </label>
        <br />
        <input
          className="form-control"
          type="text"
          id={name}
          name={name}
          value={safeDisplay()}
          disabled
        />
      </div>
    );
  }

  return (
    <div className="form-outline">
      <label className="form-label" htmlFor={name}>
        <b>{label}</b>
      </label>
      <br />
      <DatePicker
        value={value ? dayjs(value) : null}
        onChange={(newValue) =>
          onChange({
            target: {
              name,
              value: newValue ? newValue.format("YYYY-MM-DD") : "",
            },
          })
        }
        views={["year", "month", "day"]}
        inputFormat="YYYY-MM-DD"
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            fullWidth
            id={name}
            sx={{
              mt: 0.5,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
                fontSize: 14,
              },
            }}
          />
        )}
      />
    </div>
  );
};

const EmployeeView = ({ type }) => {
  const navigate = useNavigate();
  const { admin_location_id, employee_no } = useParams();
  const { permissions } = useMe();
  const pageUserType = type === "Non-Teacher" ? "non-teachers" : "teachers";
  const [employee, setEmployee] = useState(initialEmployee);
  const [needSave, setNeedSave] = useState(false);
  const [editable, setEditable] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const backUrl = `/${pageUserType}/${admin_location_id}`;
  const { handleDeleteEmployee } = useDeleteEmployee({ redirectUrl: backUrl });
  const { handleArchiveEmployee } = useArchiveEmployee({ redirectUrl: backUrl });

  const employeeUpdate = useMutation((body) => new API().updateEmployee(body), {
    onSuccess: (data) => {
      if (data?.status === 'ok') {
        toast.success("Data Updated Successfully!");
        navigate(backUrl);
      } else {
        toast.error(data?.message || "Data Update Failed!");
      }
    },
  });

  const getEmplyeeData = async () => {
    new API().getEmployee(employee_no).then((data) => {
      if (data != null) {
        setEmployee({ ...data["data"] });
      } else {
        toast.error("Invalid Request!");
        navigate(backUrl);
      }
    });
  };

  const getData = async () => {
    await getEmplyeeData();
  };

  useEffect(() => {
    void getData();
  }, [employee_no]);

  // Load dropdown options only when edit mode is active (lazy fetch → faster page load).
  // The shared cache key means subsequent visits reuse the cached result instantly.
  const { data: fieldOptions = {} } = useQuery(["staff_field_options"], {
    queryFn: async () => {
      const result = await new API().getStaffFieldOptions();
      return result?.data ?? {};
    },
    staleTime: 5 * 60 * 1000,
    enabled: editable,
  });

  // Fetch position records from the Staff list for this admin location (for auto-fill)
  const { data: positionsLookup = [] } = useQuery(
    ["positions_lookup", admin_location_id],
    {
      queryFn: async () => {
        const result = await new API().getStaffPositionsLookup(admin_location_id);
        return result?.data ?? [];
      },
      staleTime: 5 * 60 * 1000,
      enabled: editable,
    }
  );

  // Fetch only Vacant positions for the HDA dropdown
  const { data: vacantPositions = [] } = useQuery(
    ["vacant_positions_lookup", admin_location_id],
    {
      queryFn: async () => {
        const result = await new API().getVacantPositionsLookup(admin_location_id);
        return result?.data ?? [];
      },
      staleTime: 5 * 60 * 1000,
      enabled: editable || employee.hda_active === true,
    }
  );

  // Auto-fill all 12 position fields when a position is selected from the Staff list
  const handlePositionSelect = (position) => {
    if (!needSave) setNeedSave(true);
    setEmployee((prev) => ({
      ...prev,
      position_no: position.position_no ?? "",
      occup_pos_title: position.occup_pos_title ?? "",
      award: position.award ?? "",
      award_desc: position.award_desc ?? "",
      classification: position.classification ?? "",
      class_desc: position.class_desc ?? "",
      occup_type: position.occup_type ?? "",
      account_no: position.account_no ?? "",
      account_no_desc: position.account_no_desc ?? "",
      paypoint: position.paypoint ?? "",
      paypoint_desc: position.paypoint_desc ?? "",
      occup_pos_cat: position.occup_pos_cat ?? "",
    }));
  };

  const handleChange = (e) => {
    if (!needSave) setNeedSave(true);
    setEmployee({ ...employee, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    employeeUpdate.mutate(employee);
  };

  const opts = fieldOptions;

  return (
    <>
    <div className="wrapper">
      <div className="sidebar">
        <Sidebar />
      </div>
      <div className="content">
        <div className="profile">
          <div className="profileContainer">
            <div className="profileSection">
              <Link
                to={`/${pageUserType}/${admin_location_id}`}
                className="backButtonSection"
              >
                <KeyboardBackspaceIcon className="backButton" />
              </Link>
              <br />
              <br />

              <form>
                <div className="container">
                  <div className="row">
                    <div className="col col-sm">
                      <h1>View Employee Details</h1>
                    </div>
                  </div>
                  <hr />

                  {/* Profile photo */}
                  <div className="text-center">
                    <img
                      style={{
                        width: "200px",
                        height: "200px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "40px solid black",
                      }}
                      src={
                        employee.avatar_src
                          ? `${Config.api_url}v1/admin/employee/profile_image/${employee.avatar_src}`
                          : sample
                      }
                      alt=""
                    />
                  </div>
                  {permissions.super && (
                    <UploadFile
                      invalidateQueries="employees"
                      url={`${Config.api_url}v1/admin/employee/employee_avatar?employee_no=${employee_no}`}
                      name="avatar_file"
                    />
                  )}
                  <hr />

                  <div className="row">
                    {/* ── Column 1 ── */}
                    <div className="col col-sm">
                      {/* Always read-only */}
                      <PlainField label="User type"       name="user_type"     value={employee?.user_type}     disabled />
                      <PlainField label="Employee no"     name="employee_no"   value={employee?.employee_no}   disabled />
                      <PlainField label="Name report"     name="name_report"   value={employee?.name_report}   disabled />
                      <PlainField label="Admin location"  name="admin_location" value={employee?.admin_location} disabled />
                      <PlainField label="Admin desc"      name="admin_desc"    value={employee?.admin_desc}    disabled />
                      <PositionLookupField label="Position no" name="position_no" value={employee?.position_no} editable={editable} onChange={handleChange} onPositionSelect={handlePositionSelect} positions={positionsLookup} matchKey="position_no" />
                      <PositionLookupField label="Occup pos title" name="occup_pos_title" value={employee?.occup_pos_title} editable={editable} onChange={handleChange} onPositionSelect={handlePositionSelect} positions={positionsLookup} matchKey="occup_pos_title" />

                      <PlainField label="Award"      name="award"      value={employee?.award}      disabled />
                      <PlainField label="Award desc" name="award_desc" value={employee?.award_desc} disabled />
                    </div>

                    {/* ── Column 2 ── */}
                    <div className="col col-sm">
                      <PlainField label="Classification" name="classification" value={employee?.classification} disabled />
                      <PlainField label="Class desc"     name="class_desc"     value={employee?.class_desc}     disabled />

                      {/* Step no — plain text, no dropdown */}
                      <div className="form-outline">
                        <label className="form-label" htmlFor="step_no"><b>Step no</b></label>
                        <br />
                        <input className="form-control" type="text" name="step_no" id="step_no" value={employee?.step_no} disabled={!editable} onChange={(e) => handleChange(e)} />
                      </div>

                      <PlainField label="Occup type" name="occup_type" value={employee?.occup_type} disabled />

                      {/* Gender — native select (M / F only) */}
                      <div className="form-outline">
                        <label className="form-label" htmlFor="gender"><b>Gender</b></label>
                        <br />
                        <select
                          className="form-select"
                          name="gender"
                          id="gender"
                          value={employee?.gender}
                          onChange={handleChange}
                          disabled={!editable}
                        >
                          <option value="M">Male</option>
                          <option value="F">Female</option>
                        </select>
                      </div>

                      <DateField label="First commence" name="first_commence" value={employee?.first_commence} editable={editable} onChange={handleChange} />
                      <PlainField label="Account no"      name="account_no"      value={employee?.account_no}      disabled />
                      <PlainField label="Account no desc" name="account_no_desc" value={employee?.account_no_desc} disabled />
                      <DropdownField label="Emp status"      name="emp_status"      value={employee?.emp_status}      editable={editable} onChange={handleChange} options={opts.emp_status ?? []} />
                    </div>

                    {/* ── Column 3 ── */}
                    <div className="col col-sm">
                      <PlainField label="Paypoint"      name="paypoint"      value={employee?.paypoint}      disabled />
                      <PlainField label="Paypoint desc" name="paypoint_desc" value={employee?.paypoint_desc} disabled />

                      <DateField label="Date of birth" name="date_of_birth" value={employee?.date_of_birth} editable={editable} onChange={handleChange} />

                      {/* Age — always read-only */}
                      <div className="form-outline">
                        <label className="form-label" htmlFor="age"><b>Age</b></label>
                        <br />
                        <input className="form-control" type="number" name="age" id="age" value={employee?.age} disabled />
                      </div>

                      <PlainField label="Occup pos cat" name="occup_pos_cat" value={employee?.occup_pos_cat} disabled />

                      {/* Always read-only fields */}
                      <div className="form-outline">
                        <label className="form-label" htmlFor="status"><b>Status</b></label>
                        <br />
                        <input className="form-control" type="text" name="status" id="status" value={employee?.status ? "Active" : "Inactive"} disabled />
                      </div>
                      <div className="form-outline">
                        <label className="form-label" htmlFor="created_by"><b>Created by</b></label>
                        <br />
                        <input className="form-control" type="text" name="created_by" id="created_by" value={employee?.creator} disabled />
                      </div>
                      <div className="form-outline">
                        <label className="form-label" htmlFor="created_at"><b>Created at</b></label>
                        <br />
                        <input className="form-control" type="text" name="created_at" id="created_at" value={new Date(employee?.created_at).toDateString()} disabled />
                      </div>
                      <div className="form-outline">
                        <label className="form-label" htmlFor="updated_at"><b>Updated at</b></label>
                        <br />
                        <input className="form-control" type="text" name="updated_at" id="updated_at" value={new Date(employee?.updated_at).toDateString()} disabled />
                      </div>
                    </div>
                  </div>

                  {/* ── Employee Email ID ── */}
                  <div className="row mt-3">
                    <div className="col col-sm-4">
                      <div className="form-outline">
                        <label className="form-label" htmlFor="email">
                          <b>Employee Email ID</b>
                        </label>
                        <br />
                        <input
                          className="form-control"
                          type="email"
                          id="email"
                          name="email"
                          value={employee?.email ?? ""}
                          disabled={!editable}
                          onChange={handleChange}
                          pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                          title="Enter a valid email address"
                          placeholder="example@domain.com"
                        />
                      </div>
                    </div>
                    <div className="col col-sm-4">
                      <div className="form-outline">
                        <label className="form-label">
                          <b>Position Status</b>
                        </label>
                        <br />
                        <div
                          style={{
                            display: "inline-flex",
                            borderRadius: 6,
                            overflow: "hidden",
                            border: "1px solid #ccc",
                            marginTop: 4,
                            opacity: editable ? 1 : 0.6,
                            pointerEvents: editable ? "auto" : "none",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              if (!needSave) setNeedSave(true);
                              setEmployee((prev) => ({ ...prev, position_attached: true }));
                            }}
                            style={{
                              padding: "6px 18px",
                              border: "none",
                              cursor: editable ? "pointer" : "default",
                              backgroundColor: employee?.position_attached !== false ? "#4b4b9c" : "#f0f0f0",
                              color: employee?.position_attached !== false ? "#fff" : "#333",
                              fontWeight: employee?.position_attached !== false ? "bold" : "normal",
                              fontSize: 14,
                            }}
                          >
                            Attached
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (!needSave) setNeedSave(true);
                              setEmployee((prev) => ({ ...prev, position_attached: false }));
                            }}
                            style={{
                              padding: "6px 18px",
                              border: "none",
                              cursor: editable ? "pointer" : "default",
                              backgroundColor: employee?.position_attached === false ? "#c0392b" : "#f0f0f0",
                              color: employee?.position_attached === false ? "#fff" : "#333",
                              fontWeight: employee?.position_attached === false ? "bold" : "normal",
                              fontSize: 14,
                            }}
                          >
                            Unattached
                          </button>
                        </div>
                        {employee?.position_attached === false && (
                          <p style={{ color: "#c0392b", fontSize: 12, marginTop: 6 }}>
                            Position No and Occup Pos Title will be cleared and shown as "Unattached" in reports.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ── Phone / Mobile / Portal Status ── */}
                  <div className="row mt-3">
                    <div className="col col-sm-4">
                      <div className="form-outline">
                        <label className="form-label" htmlFor="phone_number">
                          <b>Phone Number</b>
                        </label>
                        <br />
                        <input
                          className="form-control"
                          type="text"
                          id="phone_number"
                          name="phone_number"
                          value={employee?.phone_number ?? ""}
                          disabled={!editable}
                          onChange={handleChange}
                          placeholder="Enter phone number"
                        />
                      </div>
                    </div>
                    <div className="col col-sm-4">
                      <div className="form-outline">
                        <label className="form-label" htmlFor="mobile_number">
                          <b>Mobile Number</b>
                        </label>
                        <br />
                        <input
                          className="form-control"
                          type="text"
                          id="mobile_number"
                          name="mobile_number"
                          value={employee?.mobile_number ?? ""}
                          disabled={!editable}
                          onChange={handleChange}
                          placeholder="Enter mobile number"
                        />
                      </div>
                    </div>
                    <div className="col col-sm-4">
                      <div className="form-outline">
                        <label className="form-label">
                          <b>Employee Portal Login Status</b>
                        </label>
                        <br />
                        <div style={{ marginTop: 8, display: "flex", gap: 24 }}>
                          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: editable ? "pointer" : "default" }}>
                            <input
                              type="radio"
                              name="status"
                              checked={employee?.status !== false}
                              onChange={() => {
                                if (!needSave) setNeedSave(true);
                                setEmployee((prev) => ({ ...prev, status: true }));
                              }}
                              disabled={!editable}
                            />
                            Active
                          </label>
                          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: editable ? "pointer" : "default" }}>
                            <input
                              type="radio"
                              name="status"
                              checked={employee?.status === false}
                              onChange={() => {
                                if (!needSave) setNeedSave(true);
                                setEmployee((prev) => ({ ...prev, status: false }));
                              }}
                              disabled={!editable}
                            />
                            Inactive
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── HDA ── */}
                  <div className="row mt-3">
                    <div className="col col-sm-4">
                      <div className="form-outline">
                        <label className="form-label">
                          <b>HDA</b>
                        </label>
                        <br />
                        <div
                          style={{
                            display: "inline-flex",
                            borderRadius: 6,
                            overflow: "hidden",
                            border: "1px solid #ccc",
                            marginTop: 4,
                            opacity: editable ? 1 : 0.65,
                            pointerEvents: editable ? "auto" : "none",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              if (!needSave) setNeedSave(true);
                              setEmployee((prev) => ({ ...prev, hda_active: true }));
                            }}
                            style={{
                              padding: "6px 20px",
                              border: "none",
                              cursor: editable ? "pointer" : "default",
                              backgroundColor: employee?.hda_active === true ? "#4b4b9c" : "#f0f0f0",
                              color: employee?.hda_active === true ? "#fff" : "#333",
                              fontWeight: employee?.hda_active === true ? "bold" : "normal",
                              fontSize: 14,
                              transition: "background 0.15s",
                            }}
                          >
                            Active
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (!needSave) setNeedSave(true);
                              setEmployee((prev) => ({ ...prev, hda_active: false, hda_position_no: "" }));
                            }}
                            style={{
                              padding: "6px 20px",
                              border: "none",
                              cursor: editable ? "pointer" : "default",
                              backgroundColor: employee?.hda_active !== true ? "#6c757d" : "#f0f0f0",
                              color: employee?.hda_active !== true ? "#fff" : "#333",
                              fontWeight: employee?.hda_active !== true ? "bold" : "normal",
                              fontSize: 14,
                              transition: "background 0.15s",
                            }}
                          >
                            Inactive
                          </button>
                        </div>
                      </div>
                    </div>

                    {employee?.hda_active === true && (
                      <div className="col col-sm-8">
                        <div className="form-outline">
                          <label className="form-label">
                            <b>HDA Position (Vacant)</b>
                          </label>
                          {editable ? (
                            <Autocomplete
                              filterOptions={limitedFilter}
                              options={vacantPositions}
                              getOptionLabel={(option) =>
                                typeof option === "string"
                                  ? option
                                  : option.position_no && option.occup_pos_title
                                  ? `${option.position_no} - ${option.occup_pos_title}`
                                  : option.position_no || option.occup_pos_title || ""
                              }
                              value={
                                vacantPositions.find(
                                  (p) => p.position_no === employee.hda_position_no
                                ) ?? null
                              }
                              isOptionEqualToValue={(opt, val) =>
                                opt.position_no === val.position_no
                              }
                              onChange={(_, newValue) => {
                                if (!needSave) setNeedSave(true);
                                setEmployee((prev) => ({
                                  ...prev,
                                  hda_position_no: newValue?.position_no ?? "",
                                }));
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  variant="outlined"
                                  placeholder="Select a vacant position..."
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      backgroundColor: "#fff",
                                      fontSize: 14,
                                    },
                                  }}
                                />
                              )}
                              sx={{ mt: 0.5 }}
                            />
                          ) : (
                            <>
                              <br />
                              <input
                                className="form-control"
                                type="text"
                                value={employee.hda_position_no ?? ""}
                                disabled
                              />
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── Organisational Structure ── */}
                  <div className="row mt-3">
                    <div className="col col-sm-4">
                      <div className="form-outline">
                        <label className="form-label" htmlFor="org_executive"><b>Executive</b></label>
                        <br />
                        <input className="form-control" type="text" id="org_executive" name="org_executive" disabled={!editable} placeholder="Executive" value={employee?.org_executive ?? ""} onChange={handleChange} />
                      </div>
                    </div>
                    <div className="col col-sm-4">
                      <div className="form-outline">
                        <label className="form-label" htmlFor="org_wing"><b>Wing</b></label>
                        <br />
                        <input className="form-control" type="text" id="org_wing" name="org_wing" disabled={!editable} placeholder="Wing" value={employee?.org_wing ?? ""} onChange={handleChange} />
                      </div>
                    </div>
                    <div className="col col-sm-4">
                      <div className="form-outline">
                        <label className="form-label" htmlFor="org_division"><b>Division</b></label>
                        <br />
                        <input className="form-control" type="text" id="org_division" name="org_division" disabled={!editable} placeholder="Division" value={employee?.org_division ?? ""} onChange={handleChange} />
                      </div>
                    </div>
                  </div>
                  <div className="row mt-3">
                    <div className="col col-sm-4">
                      <div className="form-outline">
                        <label className="form-label" htmlFor="org_branch"><b>Branch</b></label>
                        <br />
                        <input className="form-control" type="text" id="org_branch" name="org_branch" disabled={!editable} placeholder="Branch" value={employee?.org_branch ?? ""} onChange={handleChange} />
                      </div>
                    </div>
                    <div className="col col-sm-4">
                      <div className="form-outline">
                        <label className="form-label" htmlFor="org_unit"><b>Unit</b></label>
                        <br />
                        <input className="form-control" type="text" id="org_unit" name="org_unit" disabled={!editable} placeholder="Unit" value={employee?.org_unit ?? ""} onChange={handleChange} />
                      </div>
                    </div>
                  </div>

                  <hr />
                  <Stack direction="row">
                    {permissions.super && (
                      <Link
                        className="app-button app-button-gray"
                        to={`/${pageUserType}/${admin_location_id}`}
                        style={{ marginRight: 10, marginLeft: 10 }}
                      >
                        Back
                      </Link>
                    )}
                    {permissions.super && (
                      <button
                        className="app-button app-button-primary"
                        style={{ marginRight: 10 }}
                        disabled={editable}
                        onClick={(e) => { e.preventDefault(); setEditable(true); }}
                      >
                        Edit
                      </button>
                    )}
                    {permissions.super && (
                      <button
                        className="app-button app-button-primary"
                        style={{ marginRight: 10 }}
                        disabled={!needSave}
                        onClick={(e) => handleSubmit(e)}
                      >
                        Save
                      </button>
                    )}
                    {permissions.super && (
                      <button
                        className="app-button app-button-primary"
                        style={{ marginRight: 10 }}
                        onClick={(e) => handleDeleteEmployee(e, employee_no)}
                      >
                        Delete
                      </button>
                    )}
                    {permissions.super && (
                      <button
                        className="app-button app-button-primary"
                        style={{ marginRight: 10 }}
                        onClick={(e) => handleArchiveEmployee(e, employee_no)}
                      >
                        Archive
                      </button>
                    )}
                    {permissions.super && (
                      <EmployeeTransfer refetchData={getData} employee={employee} type={type} />
                    )}
                    {permissions.super && (
                      <button
                        className="app-button app-button-primary"
                        style={{ marginRight: 10 }}
                        onClick={(e) => { e.preventDefault(); setShowPasswordReset(true); }}
                      >
                        Password Reset
                      </button>
                    )}
                  </Stack>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>

    {showPasswordReset && (
      <PasswordResetModal
        employee_no={employee_no}
        onClose={() => setShowPasswordReset(false)}
      />
    )}
    </>
  );
};

export default EmployeeView;
